import { decodeToken } from "react-jwt";

/**
 * Validates if a token is valid and not expired
 * @param {string} token - The JWT token to validate
 * @returns {boolean} - True if token is valid, false otherwise
 */
export const isValidToken = (token) => {
  if (!token) return false;

  try {
    const decoded = decodeToken(token);
    if (!decoded) return false;

    // Check if token has required fields
    if (!decoded._id) return false;

    // Check if token is expired (if it has exp field)
    if (decoded.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime > decoded.exp) return false;
    }

    return true;
  } catch (error) {
    console.error("Token validation error:", error);
    return false;
  }
};

/**
 * Extracts user information from a token
 * @param {string} token - The JWT token
 * @returns {object|null} - User information or null if invalid
 */
export const getUserFromToken = (token) => {
  if (!isValidToken(token)) return null;

  try {
    const decoded = decodeToken(token);
    return {
      id: decoded._id,
      email: decoded.email,
      userType: decoded.userType || "unknown",
    };
  } catch (error) {
    console.error("Error extracting user from token:", error);
    return null;
  }
};

/**
 * Removes token from URL and updates browser history
 * @param {object} history - React Router history object
 */
export const removeTokenFromUrl = (history) => {
  const newUrl = window.location.pathname;
  history.replace(newUrl);
};

/**
 * Checks if current URL has a token parameter
 * @returns {boolean} - True if URL has token parameter
 */
export const hasTokenInUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has("token");
};

/**
 * Gets token from URL parameters
 * @returns {string|null} - Token from URL or null if not found
 */
export const getTokenFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("token");
};

/**
 * Adds token to URL if token exists
 * @param {string} baseUrl - The base URL to add token to
 * @returns {string} - URL with token parameter if token exists
 */
export const addTokenToUrl = (baseUrl) => {
  const token = localStorage.getItem("authorization");
  if (token) {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
  }
  return baseUrl;
};
